<template>
    <div class="row">
        <div class="small-12 columns purchase-date-input-container" :class="{ error: showPurchaseDateErrorText }">
            <label>
                Kaufdatum des Fahrzeugs
                <Datepicker
                    class="purchase-date-input"
                    v-model="formData.purchase_date"
                    :upper-limit="viewData.datepickerOptions.upperLimit"
                    :input-format="viewData.datepickerOptions.inputFormat"
                    :locale="viewData.datepickerOptions.locale"
                    placeholder="Bitte Datum wählen..."
                />
            </label>
            <small class="error" v-if="showPurchaseDateErrorText">
                {{ purchaseDateErrorText }}
            </small>
        </div>
        <template v-if="showPurchaseDateHint">
            <div class="small-12 columns">
                <div class="panel radius">
                    <h4 class="text-align-center font-size-small font-weight-bold">Wichtiger Hinweis</h4>
                    <p class="font-size-small">
                        Das deutsche Recht sieht vor, dass Schadensersatzansprüche ohne Rücksicht auf die Kenntnis in zehn
                        Jahren von ihrer Entstehung an verjähren.
                        <br/>
                        Bereits in anderem Zusammenhang hat der EuGH allerdings entschieden, dass eine Verjährungsfrist nur
                        dann mit dem Effektivitätsgrundsatz vereinbar sein kann, wenn der Berechtigte die Möglichkeit hatte,
                        von seinen Rechten Kenntnis zu nehmen, bevor diese Frist zu laufen beginnt oder abgelaufen ist. Wird
                        dem Ersatzanspruch wegen der Verwendung eines unzulässigen Abschalteinrichtung, eine zehnjährige
                        Verjährungsfrist entgegengehalten, die ab Kauf des Fahrzeugs zu laufen beginnt, kann ein wirksamer
                        Schutz des Berechtigten möglicherweise nicht gewährleistet werden, da diese Frist abgelaufen sein
                        könnte, bevor der Berechtigte die Möglichkeit hatte, von der Unzulässigkeit der in seinem Fahrzeug
                        verbauten Abschalteinrichtung Kenntnis zu nehmen. Eine solche Frist könnte deshalb die Ausübung der
                        diesem durch die RL 2007/46/EG verliehenen Rechte übermäßig erschweren und würde dann gegen den
                        Effektivitätsgrundsatz verstoßen.
                    </p>
                </div>
            </div>
        </template>
        <div class="small-12 columns" :class="{ error: showPriceErrorText }">
            <label>
                Kaufpreis
                <currency-input v-model="formData.price" :options="viewData.currencyInputOptions"/>
            </label>
            <small class="error" v-if="showPriceErrorText">
                {{ priceErrorText }}
            </small>
        </div>

        <div class="small-12 columns" :class="{ error: showWasNewErrorText }">
            <label>
                War das Fahrzeug ein Neuwagen?
                <select v-model="formData.was_new">
                    <option
                        v-for="selectValue in defaultSelectValues"
                        :key="selectValue.value"
                        :value="selectValue.value"
                    >
                        {{selectValue.title}}
                    </option>
                </select>
            </label>
            <small class="error" v-if="showWasNewErrorText">
                {{ wasNewErrorText }}
            </small>
        </div>
        <div v-if="showMileagePurchaseInput" class="small-12 columns" :class="{ error: showMileagePurchaseErrorText }">
            <label>
                Kilometerstand bei Kauf
                <input type="number" v-model="formData.mileage_purchase"/>
            </label>
            <small class="error" v-if="showMileagePurchaseErrorText">
                {{ mileagePurchaseErrorText }}
            </small>
        </div>
        <div class="small-12 columns" :class="{ error: showPurchaseContractErrorText }">
            <label>
                Vollständiger Kaufvertrag als PDF-Datei
                <input ref="purchaseContractInput" type="file" accept="application/pdf" @change.stop="onChangePurchaseContract"/>
            </label>
            <small class="error" v-if="showPurchaseContractErrorText">
                {{ purchaseContractErrorText }}
            </small>
        </div>
        <div v-if="showSalesContractInput" class="small-12 columns" :class="{ error: showSalesContractErrorText }">
            <label>
                Vollständiger Verkaufsvertrag als PDF-Datei
                <input ref="salesContractInput" type="file" accept="application/pdf" @change.stop="onChangeSalesContract"/>
            </label>
            <small class="error" v-if="showSalesContractErrorText">
                {{ salesContractErrorText }}
            </small>
        </div>
    </div>
</template>

<script>
import TypeHelper from '../../helper/TypeHelper';
import Validation from '../../helper/Validation';
import CurrencyInput from '../CurrencyInput';
import Datepicker from 'vue3-datepicker';
import de from 'date-fns/locale/de';
import moment from 'moment';
import FileHelper from '../../helper/FileHelper';

export default {
    name: 'PurchaseDataFormStep',
    components: { CurrencyInput, Datepicker },
    props: {
        caseData: {
            type: Object,
            default: function()
            {
                return {
                    mandator_lawform:                       null,
                    mandator_prename:                       null,
                    mandator_surname:                       null,
                    mandator_mail:                          null,
                    mandator_iban:                          null,
                    second_mandator_lawform:                null,
                    second_mandator_prename:                null,
                    second_mandator_surname:                null,
                    manufacturer_id:                        null,
                    purchase_date:                          null,
                    emissions_type:                         null,
                    mileage_purchase:                       null,
                    mileage:                                null,
                    price:                                  null,
                    is_vehicle_sold:                        null,
                    was_new:                                null,
                    selected_compensation:                  null
                }
            }
        }
    },
    data: () => ({
        formData: {
            purchase_date:              null,
            price:                      null,
            mileage_purchase:           null,
            was_new:                    null
        },
        purchaseContract: null,
        salesContract: null,
        validation: {
            isFormDirty: false,
            isPurchaseDateDirty: false,
            isPriceDirty: false,
            isMileagePurchaseDirty: false,
            isWasNewDirty: false,
            isPurchaseContractDirty: false,
            isSalesContractDirty: false
        },
        constants: {
            SELECT_VALUE_YES: {
                value: true,
                title: 'Ja'
            },
            SELECT_VALUE_FALSE: {
                value: false,
                title: 'Nein'
            },
            PURCHASE_CONTRACT_DOCUMENT_TITLE: 'Vollständiger Kaufvertrag',
            SALES_CONTRACT_DOCUMENT_TITLE: 'Vollständiger Verkaufsvertrag',
        },
        viewData: {
            currencyInputOptions: {
                "currency": "EUR",
                "currencyDisplay": "symbol",
                "valueRange": {
                    "min": 0
                },
                "hideCurrencySymbolOnFocus": true,
                "hideGroupingSeparatorOnFocus": true,
                "hideNegligibleDecimalDigitsOnFocus": true,
                "autoDecimalDigits": false,
                "autoSign": true,
                "useGrouping": true,
                "accountingSign": false
            },
            datepickerOptions: {
                upperLimit: new Date(),
                inputFormat: 'dd.MM.yyyy',
                locale: de
            }
        }
    }),
    computed: {
        showPurchaseDateHint()
        {
            let today = moment();
            let purchaseDate = moment(this.formData.purchase_date);
            return TypeHelper.isNotNullOrUndefined(this.formData.purchase_date) && today.diff(purchaseDate, 'years') >= 10;
        },
        defaultSelectValues()
        {
            return [
                this.constants.SELECT_VALUE_YES,
                this.constants.SELECT_VALUE_FALSE
            ];
        },
        showMileagePurchaseInput()
        {
            return this.formData.was_new === false;
        },
        showSalesContractInput()
        {
            return this.caseData.is_vehicle_sold;
        },
        showPurchaseDateErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isPurchaseDateDirty)
                && TypeHelper.isNotNullOrUndefined(this.purchaseDateErrorText);
        },
        showPriceErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isPriceDirty)
                && TypeHelper.isNotNullOrUndefined(this.priceErrorText);
        },
        showWasNewErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isWasNewDirty)
                && TypeHelper.isNotNullOrUndefined(this.wasNewErrorText);
        },
        showMileagePurchaseErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isMileagePurchaseDirty)
                && TypeHelper.isNotNullOrUndefined(this.mileagePurchaseErrorText);
        },
        showPurchaseContractErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isPurchaseContractDirty)
                && TypeHelper.isNotNullOrUndefined(this.purchaseContractErrorText);
        },
        showSalesContractErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isSalesContractDirty)
                && TypeHelper.isNotNullOrUndefined(this.salesContractErrorText);
        },
        purchaseDateErrorText()
        {
            let validators = [
                Validation.isNotNull
            ];

            return Validation.runValidation(this.formData.purchase_date, validators);
        },
        priceErrorText()
        {
            let validators = [
                Validation.isNotNull
            ];

            return Validation.runValidation(this.formData.price, validators);
        },
        wasNewErrorText()
        {
            let validators = [
                Validation.requiresSelect
            ];

            return Validation.runValidation(this.formData.was_new, validators);
        },
        mileagePurchaseErrorText()
        {
            let validators = [];

            if (this.formData.was_new === false)
                validators.push(Validation.isNotNull, Validation.isNumeric);

            let validationResult = Validation.runValidation(this.formData.mileage_purchase, validators);

            if (TypeHelper.isNullOrUndefined(validationResult)
                && TypeHelper.isNotNullOrUndefined(this.caseData.mileage)
                && this.formData.mileage_purchase > this.caseData.mileage)
                validationResult = 'Der Kilometerstand bei Kauf darf nicht größer als der aktuelle/letzte Kilometerstand sein';

            return validationResult;
        },
        purchaseContractErrorText()
        {
            let validators = [
                Validation.requiresFile
            ];

            let validationResult = Validation.runValidation(this.purchaseContract, validators);

            if (TypeHelper.isNullOrUndefined(validationResult)
                && TypeHelper.isNotNullOrUndefined(this.purchaseContract)
                && this.purchaseContract.mime !== 'application/pdf')
                validationResult = 'Bitte wählen Sie nur PDF-Dateien aus';

            return validationResult;
        },
        salesContractErrorText()
        {
            let validators = [];
            let validationResult = null;
            if (this.caseData.is_vehicle_sold)
            {
                validators.push(Validation.requiresFile);

                validationResult = Validation.runValidation(this.salesContract, validators);

                if (TypeHelper.isNullOrUndefined(validationResult)
                    && TypeHelper.isNotNullOrUndefined(this.salesContract)
                    && this.salesContract.mime !== 'application/pdf')
                    validationResult = 'Bitte wählen Sie nur PDF-Dateien aus';
            }

            return validationResult;
        }
    },
    methods: {
        validate()
        {
            this.validation.isFormDirty = true;

            this.emitValidity();
        },
        emitValidity()
        {
            this.$emit('updateValidity', this.isFormValid());
        },
        isFormValid()
        {
            let isValid = true;

            isValid = isValid && (this.purchaseDateErrorText === null);
            isValid = isValid && (this.priceErrorText === null);
            isValid = isValid && (this.wasNewErrorText === null);
            isValid = isValid && (this.mileagePurchaseErrorText === null);
            isValid = isValid && (this.purchaseContractErrorText === null);
            isValid = isValid && (this.salesContractErrorText === null);

            return isValid;
        },
        emitChangedData()
        {
            this.$emit('changeCaseData', this.formData);
        },
        emitPurchaseContract()
        {
            this.$emit('changePurchaseContract', this.purchaseContract);
        },
        emitSalesContract()
        {
            this.$emit('changeSalesContract', this.salesContract);
        },
        async onChangePurchaseContract()
        {
            let file = this.$refs.purchaseContractInput.files[0];
            if (TypeHelper.isNotNullOrUndefined(file))
            {
                let fileBase64 = await FileHelper.readFileAsync(file);
                this.purchaseContract = {
                    title: this.constants.PURCHASE_CONTRACT_DOCUMENT_TITLE + FileHelper.resolveMimeToFileEnding(file.type),
                    mime: file.type,
                    data_base64: FileHelper.cutBase64ApplicationData(fileBase64)
                }
            }
            else
                this.purchaseContract = null;
        },
        async onChangeSalesContract()
        {
            let file = this.$refs.salesContractInput.files[0];
            if (TypeHelper.isNotNullOrUndefined(file))
            {
                let fileBase64 = await FileHelper.readFileAsync(file);
                this.salesContract = {
                    title: this.constants.SALES_CONTRACT_DOCUMENT_TITLE + FileHelper.resolveMimeToFileEnding(file.type),
                    mime: file.type,
                    data_base64: FileHelper.cutBase64ApplicationData(fileBase64)
                }
            }
            else
                this.salesContract = null;
        }
    },
    watch: {
        'caseData.mileage': function()
        {
            this.emitValidity();
        },
        'caseData.is_vehicle_sold': function()
        {
            this.salesContract = null;
            this.validation.isSalesContractDirty = false;
            this.emitValidity();
        },
        'formData.purchase_date': function()
        {
            this.validation.isPurchaseDateDirty = true;
        },
        'formData.price': function()
        {
            this.validation.isPriceDirty = true;
        },
        'formData.was_new': function ()
        {
            this.validation.isWasNewDirty = true;

            this.formData.mileage_purchase = null;
        },
        'formData.mileage_purchase': function()
        {
            this.validation.isMileagePurchaseDirty = true;
        },
        formData: {
            handler: function()
            {
                this.emitChangedData();
                this.emitValidity();
            },
            deep: true
        },
        salesContract: function()
        {
            this.validation.isSalesContractDirty = true;
            this.emitSalesContract();
            this.emitValidity();
        },
        purchaseContract: function()
        {
            this.validation.isPurchaseContractDirty = true;
            this.emitPurchaseContract();
            this.emitValidity();
        }
    }
};
</script>

<style scoped>
.error label {
    color: #4d4d4d;
}

select {
    margin: 0 0 1rem;
}

.error select {
    margin-bottom: 0;
}

.v3dp__datepicker {
    --vdp-hover-bg-color: #e10065;
    --vdp-selected-bg-color: #e10065;
}

.text-align-center {
    text-align: center;
}

.font-size-small {
    font-size: small;
}

.font-weight-bold {
    font-weight: bold;
}

.purchase-date-input {
    margin-bottom: 0px!important;
}

.purchase-date-input.error {
    margin-bottom: 0px !important;
}

.purchase-date-input-container .error {
    margin-bottom: 2px;
}
</style>
